import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  Link as ChakraLink,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { collection, getCountFromServer, getDocs, limit, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { db } from '../firebaseHandler'
import { WeekDocument } from '../types'
import { ENV } from './ScoreListing'

type WeekListItem = Partial<WeekDocument> & { id: string; scoreCount?: number }

function Root() {
  const [weeks, setWeeks] = useState<WeekListItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const fetchWeeks = async (): Promise<void> => {
      try {
        const weeksQuery = query(
          collection(db, `scores_${ENV}`),
          orderBy('weekNum', 'desc'),
          limit(12)
        )
        const snapshot = await getDocs(weeksQuery)
        const items = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const baseData = {
              id: docSnap.id,
              ...(docSnap.data() as Partial<WeekDocument>),
            }

            try {
              const scoresCollection = collection(db, `${docSnap.ref.path}/scores`)
              const countSnapshot = await getCountFromServer(scoresCollection)
              return { ...baseData, scoreCount: countSnapshot.data().count ?? 0 }
            } catch (countError) {
              console.error('Failed to load score count for', docSnap.id, countError)
              return baseData
            }
          })
        )
        setWeeks(items)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error while fetching weeks.'
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchWeeks()
  }, [])

  const renderBody = () => {
    if (isLoading) {
      return (
        <Center py={20} flexDirection="column" gap={4} color="gray.600">
          <Spinner size="xl" />
          <Text>Loading the latest weeks…</Text>
        </Center>
      )
    }

    if (error) {
      return (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )
    }

    if (weeks.length === 0) {
      return (
        <Text>No week data available yet. Create a week from the Scores page to get started.</Text>
      )
    }

    return (
      <SimpleGrid columns={[1, 1, 2, 2, 3]} spacing={6} mt={6}>
        {weeks.map((week) => (
          <Box key={week.id} borderWidth="1px" borderRadius="lg" p={5} boxShadow="md" bg="white">
            <Stack spacing={3} h="100%" justify="space-between">
              <div>
                <Heading as="h3" size="md">
                  Week {week.weekNum ?? week.id.replace('week', '')}
                </Heading>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  {week.lastScraped
                    ? `Last scraped: ${formatTimestamp(week.lastScraped)}`
                    : 'Not scraped yet'}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {typeof week.scoreCount === 'number'
                    ? `Scores available: ${week.scoreCount}`
                    : 'Scores: unavailable'}
                </Text>
              </div>
              <Stack spacing={2}>
                <Button
                  as={RouterLink}
                  to={`/scores/${week.weekNum ?? week.id.replace('week', '')}`}
                  colorScheme="teal"
                >
                  Open validator
                </Button>
                {week.url && (
                  <Button
                    as={ChakraLink}
                    href={week.url}
                    isExternal
                    variant="ghost"
                    colorScheme="gray"
                    rightIcon={<Icon as={ExternalLinkIcon} />}
                  >
                    View forum thread
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
    )
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Center bg="tomato" h="60px" color="white" px={4} boxShadow="sm">
        <HStack spacing={3}>
          <Heading as="h1" size="lg">
            Rocksmith Champ Score Assistant
          </Heading>
          <Text fontWeight="medium">Latest weeks overview</Text>
        </HStack>
      </Center>

      <Box p={[6, 8, 10]}>
        <Stack spacing={4} mb={4}>
          <Heading as="h2" size="md">
            Recent championship weeks
          </Heading>
          <Text color="gray.600">
            Showing the most recent championship weeks. Use the quick links below to jump straight
            into the validator or open the original forum thread.
          </Text>
        </Stack>

        {renderBody()}
      </Box>
    </Box>
  )
}

function formatTimestamp(value?: WeekDocument['lastScraped']): string {
  if (!value) {
    return 'Unknown'
  }

  if (typeof value === 'string') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
      return formatDate(parsed)
    }
    return value
  }

  if ('toDate' in value && typeof value.toDate === 'function') {
    return formatDate(value.toDate())
  }

  return String(value)
}

function formatDate(date: Date): string {
  return date.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default Root
